import React from "react";
import { useMenu } from "../../context/MenuContext";
import { getModuleName } from "../../utils/commonHelper";
import BackdropProgressBar from "../ui/BackdropProgressBar";
import {
  ExcludeSideNavModuleSet,
  ModuleNames,
  WORKFLOW_STATIC_UI_BYPASS,
} from "../../data/constants";
import NotAuthorized from "../../pages/error/NotAuthorized";

const MenuGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { menu, permissions, loading } = useMenu();
  const moduleName = getModuleName()?.toLowerCase();

  if (loading) return <BackdropProgressBar loading={loading} />;
  if (
    WORKFLOW_STATIC_UI_BYPASS &&
    (moduleName === ModuleNames.WorkflowLibrary.toLowerCase() ||
      moduleName === ModuleNames.WorkflowBuilder.toLowerCase() ||
      moduleName === ModuleNames.WorkflowInstances.toLowerCase() ||
      moduleName === ModuleNames.WorkflowMyWork.toLowerCase())
  ) {
    return <>{children}</>;
  }

  // Instances and My Work are one screen. Either seeded module name may grant it;
  // the page shows both panels.
  if (moduleName === ModuleNames.WorkflowInstances.toLowerCase()) {
    const canViewWorkspace = permissions.some(
      permission =>
        (permission.moduleName === ModuleNames.WorkflowInstances ||
          permission.moduleName === ModuleNames.WorkflowMyWork) &&
        permission.canView,
    );
    return canViewWorkspace ? <>{children}</> : <NotAuthorized isAccessMenu={true} />;
  }

  if (menu.length === 0) {
    return <NotAuthorized isAccessMenu={false} />;
  }

  const allUrls = menu.flatMap(section =>
    section.listItems.map(item => item.moduleName?.toLowerCase())
  );

  if (moduleName === ModuleNames.Messages.toLowerCase()) {
    const isMessageEnable = permissions?.find(permission =>
      permission.moduleName === ModuleNames.Messages)?.canView;
    if (!isMessageEnable) {
      return <NotAuthorized  isAccessMenu={true}/>;
    }
  }

  // The PostIF form is opened by URL - from the start screen, a resumed draft, or a
  // returned-for-changes link - and is deliberately NOT a side-nav entry, so the
  // menu-membership check below would reject it for every user. Gate it on its own CanEdit
  // instead: Epic 79767 AC5 keeps the Form permission separate from PostIF-MyForms and
  // PostIF-List so a role can fill a form without being able to see either list.
  if (moduleName === ModuleNames.PostIFForm.toLowerCase()) {
    const canEditForm = permissions?.find(
      permission => permission.moduleName === ModuleNames.PostIFForm)?.canEdit;
    return canEditForm ? <>{children}</> : <NotAuthorized isAccessMenu={true} />;
  }

  // Epic 80427: Test/Run is not a System_Modules row. RouteKeywords maps /workflow/run to
  // WorkflowBuilder, and test-running a definition is Builder CanEdit. Menu membership is only
  // CanView, so this route checks CanEdit the same way the PostIF form does above.
  // Epic 80430: opening an existing case (?instanceId=) is the Instances / My Work audience's job,
  // not a Builder's, so either of those grants opens it too. Starting a case outside the Builder
  // (Library Run, Instances "New case") needs My Work CanEdit — the grant API PR 32162 moves
  // instance/start onto — so a Hiring Supervisor can start a PIF without Builder access. A Builder
  // test run (source=builder) stays Builder CanEdit. The API still decides per request: a case the
  // user may not view gets the screen's own "no access" state.
  if (
    moduleName === ModuleNames.WorkflowBuilder.toLowerCase() &&
    window.location.pathname.toLowerCase().includes("/workflow/run")
  ) {
    const runParams = new URLSearchParams(window.location.search);
    // A share link (/wf/{RefNo} -> ?refNo=) opens a case too (decision 6.5).
    const opensCase = runParams.has("instanceId") || runParams.has("refNo");
    const startsCase = !opensCase && runParams.get("source") !== "builder";
    const canRun = permissions?.some(
      permission =>
        (permission.moduleName === ModuleNames.WorkflowBuilder && permission.canEdit) ||
        (opensCase &&
          (permission.moduleName === ModuleNames.WorkflowInstances ||
            permission.moduleName === ModuleNames.WorkflowMyWork) &&
          permission.canView) ||
        (startsCase && permission.moduleName === ModuleNames.WorkflowMyWork && permission.canEdit),
    );
    return canRun ? <>{children}</> : <NotAuthorized isAccessMenu={true} />;
  }

  if (!allUrls.includes(moduleName) && !ExcludeSideNavModuleSet.has(moduleName)) {
    return <NotAuthorized isAccessMenu={true}/>;
  }

  return <>{children}</>;
};

export default MenuGuard;
